import React, { useCallback, useContext, useEffect } from 'react'
import { createBrandKit } from '@/services/brand-kit'
import { useQueryClient } from '@tanstack/react-query'

import { queryKeys } from '@/lib/react-query'
import { useGetBrandKits } from '@/resolvers/org/brand-kit/query'
import useAuth from '@/hooks/useAuth'
import { useReqError } from '@/hooks/useReqError'
import { BASE_PALETTE_COLOR, BrandKit } from '@/shared/brand-kit'
import { getRandomInt } from '@/shared/utils'

import { useCurrentOrgContext } from './CurrentOrgContext'

interface BrandKitContextValue {
  brandKits: BrandKit[]
  isLoading: boolean
  refetchList: () => void
  onUpdateBrand: (brandId: string, updated: BrandKit) => void
  onDeleteBrand: (brandId: string) => void
}

const BrandKitContext = React.createContext<BrandKitContextValue>({
  /**
   * Org brand kit list
   */
  brandKits: [],
  /**
   * brand kits loading indicator
   */
  isLoading: false,
  /**
   * Callback invoked after updating a brand
   */
  onUpdateBrand: () => null,
  /**
   * Callback invoked after deleteing a brand
   */
  onDeleteBrand: () => null,
  /**
   * Manual refetch org brand kits query after specific actions
   */
  refetchList: () => null,
})

/**
 * Org Brand Kits Provider
 */
export function BrandKitProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient()

  const { currentOrg } = useCurrentOrgContext()
  const currentOrgId = currentOrg?.id

  const { userData } = useAuth()
  const printAPIError = useReqError()

  // Access org brand kits
  const { data, isLoading, isFetched, refetch } = useGetBrandKits()

  // Derived data from brandkits
  const brandKits = data || []
  const noBrandKit = isFetched && !brandKits.length

  const handleCreateDefaultBrandKit = useCallback(async () => {
    if (!currentOrg || !userData) return

    // set random color pallete
    const randomKey = getRandomInt(0, Object.keys(BASE_PALETTE_COLOR).length)
    const randomColor = BASE_PALETTE_COLOR[randomKey].color

    // set default brandkit name baset on their name
    const defaultName = `${userData.name}'s brand kit`

    try {
      // create brandkit
      await createBrandKit({
        orgId: currentOrg.id,
        name: defaultName,
        color: randomColor,
        logo: null,
        logoExt: '',
      })

      // refetch query
      await refetch()
      //
    } catch (err) {
      printAPIError(err as any)
    }
  }, [currentOrg, printAPIError, refetch, userData])

  /**
   * Check if org has its brand kit, if none we'll create a default one
   */
  useEffect(() => {
    if (noBrandKit) {
      handleCreateDefaultBrandKit()
    }
  }, [noBrandKit, handleCreateDefaultBrandKit])

  const onUpdateBrand = useCallback(
    (brandId: string, updated: BrandKit) => {
      queryClient.setQueryData([queryKeys.orgBrandKits, currentOrgId], (prev: BrandKit[]) => {
        const idx = prev.findIndex((bk) => bk.id === brandId)
        if (idx !== -1) {
          const copy = [...prev]
          copy[idx] = updated
          return copy
        }
        return prev
      })
    },
    [currentOrgId, queryClient]
  )

  const onDeleteBrand = useCallback(
    (deletedId: string) => {
      queryClient.setQueryData([queryKeys.orgBrandKits, currentOrgId], (prev: BrandKit[]) => {
        return prev.filter((b) => b.id !== deletedId)
      })
      queryClient.invalidateQueries({ queryKey: [queryKeys.orgBrandKitsV2, currentOrgId] })
    },

    [currentOrgId, queryClient]
  )

  const refetchList = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: [queryKeys.orgBrandKits, currentOrgId],
    })
  }, [currentOrgId, queryClient])

  return (
    <BrandKitContext.Provider
      value={{
        brandKits,
        isLoading,
        refetchList,
        onUpdateBrand,
        onDeleteBrand,
      }}
    >
      {children}
    </BrandKitContext.Provider>
  )
}

/**
 * Org Brand Kits Context
 *
 * use this context to access and modify org brand kits
 */
export function useBrandKitContext() {
  const context = useContext(BrandKitContext)

  if (context === undefined) {
    throw new Error('useBrandKitContext must be used within a BrandKitProvider')
  }

  return context
}
